/**
 * member-avatar.js — 會員「修改個人資料」頭像本機預覽（純前端 demo，不傳後端）
 *
 * 對照 HTML（src/pages/member/edit.html）頭像卡：
 *   <div class="member-avatar" data-member-avatar>
 *     <span class="member-avatar__frame">
 *       <img class="member-avatar__img" ... hidden />        ← 預覽圖（預設 hidden，選圖後顯）
 *       <span class="member-avatar__initial">王</span>        ← 無圖時的字首 fallback
 *     </span>
 *     <label class="member-avatar__camera" for="member-avatar-input">…相機 icon…</label>
 *     <input class="member-avatar__input visually-hidden" type="file" id="member-avatar-input"
 *            accept="image/*" />
 *   </div>
 *
 * 功能（MEMBER-SPEC，F2）：
 *   - 選圖 → FileReader.readAsDataURL → 替換 .member-avatar__img src 並顯示、隱藏字首 fallback。
 *   - 最低限度防呆：非 image/* 或 >5MB 擋下並提示（不真正上傳）。
 *   - 純預覽：不送後端，不依賴 admin-form-edit.js 的 .upload-field 矩形預覽引擎
 *     （頭像為圓形 + 字首 fallback，與文件/封面上傳語彙不同，故獨立小模組）。
 *
 * a11y：input 有對應 <label>（相機鈕）且鍵盤可達（Enter/Space 開檔案選擇器）；
 *       錯誤訊息走 role=alert 的 .member-avatar__error（lazy 建立）。
 *
 * 載入：由 /src/admin.js 中央 import 鏈 auto-init（會員頁殼亦走 admin.js）。
 *   無對應 DOM（[data-member-avatar] 不存在）即 no-op（不影響其他 admin/member/auth 頁）。
 *
 * TODO(backend): 純本機預覽（FileReader），不送後端；接後端時於 change 後補上傳請求。
 */

const MAX_MB = 5;

function init(root = document) {
  const wrap = root.querySelector('[data-member-avatar]');
  if (!wrap) return; // no-op：非含頭像卡的頁

  const input = wrap.querySelector('.member-avatar__input');
  const img = wrap.querySelector('.member-avatar__img');
  const initial = wrap.querySelector('.member-avatar__initial');
  if (!input || !img) return;

  // 錯誤訊息掛在文字欄（__body）內、提示下方；無 __body 時退回 wrap。
  const errorHost = wrap.querySelector('.member-avatar__body') || wrap;

  const setError = (msg) => {
    let errorEl = wrap.querySelector('.member-avatar__error');
    if (!errorEl) {
      errorEl = document.createElement('p');
      errorEl.className = 'member-avatar__error';
      errorEl.setAttribute('role', 'alert');
      errorHost.appendChild(errorEl);
    }
    errorEl.textContent = msg;
    errorEl.hidden = false;
  };
  const clearError = () => {
    const errorEl = wrap.querySelector('.member-avatar__error');
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }
  };

  // TODO(backend): change 後補 POST multipart 上傳頭像、回 URL 持久化 — 見 docs/BACKEND_HANDOFF.md#member-avatar-upload
  input.addEventListener('change', () => {
    const file = input.files && input.files[0];
    if (!file) return;

    // 防呆：型別（accept="image/*" 為 UI 過濾，仍以 file.type 二次把關）
    if (!file.type.startsWith('image/')) {
      input.value = '';
      setError('請選擇圖片檔（JPG / PNG 等）');
      return;
    }
    // 防呆：大小
    if (file.size > MAX_MB * 1024 * 1024) {
      input.value = '';
      setError(`圖片超過 ${MAX_MB} MB，請壓縮後再試`);
      return;
    }
    clearError();

    const reader = new FileReader();
    reader.onload = (ev) => {
      img.src = ev.target.result;
      img.hidden = false;
      if (initial) initial.hidden = true;
    };
    reader.readAsDataURL(file);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}

export { init };
